namespace Application.Models {
    'use strict';

    export interface IBlockComplete extends Models.IBlock {
        type?: string;
        networkNode?: Models.INetworkNode;
        categories?: Models.ICategory[];
        deviceGui?: Models.IDeviceGui;
        videoFeed?: Models.IVideoFeed;
    }

    export interface IMatrixRoomConnection {
        streamPipeHref: string;
        source: number;
        sink: number;
    }

    export interface IConnectedBlocksMap {
        [id: number]: IConnectedBlock[];
    }

    export interface IConnectedBlock {
        id: number;
        name: string;
        href?: string;
    }

    export interface IMatrixRoomInfo {
        blocks: IBlockComplete[];
        categories: ICategory[];
        connectedBlocks: IConnectedBlocksMap;
        totalElements: number;
        hasMore: boolean;
    }

    interface IConnectionMap {
        [key: string]: number;
    }

    export class MatrixRoom {
        public connectionRoom: Models.IConnectionRoom;
        public sources: IBlockComplete[];
        public destinations: IBlockComplete[];
        public matrixRoomConnectedBlocks: IConnectedBlocksMap;
        public sourcesLoaded: boolean;
        public destinationsLoaded: boolean;
        public backgroundSourcesLoaded: boolean;
        public backgroundDestinationsLoaded: boolean;
        public hasMoreSources: boolean;
        public hasMoreDestinations: boolean;
        public sourceBlockFilter: BlocksFilter;
        public destinationBlockFilter: BlocksFilter;
        public streamPipeIdsForDestinations: Models.IStreamIdMap;
        public levelMaps: Models.LevelMaps;
        public allCategories: Models.ICategory[];

        constructor(connectionRoom: Models.IConnectionRoom,
                    private connectionRoomService: Services.ConnectionRoomService,
                    private blockService: Services.BlockService,
                    private streamPipeService: Services.StreamPipeService,
                    private $cookies: ng.cookies.ICookiesService,
                    private $q: ng.IQService,
                    private $filter: ng.IFilterService) {

            this.levelMaps = new Models.LevelMaps(blockService);
            this.getAllCategories();
            this.connectionRoom = angular.copy(connectionRoom);
            this.updateStreamPipeIdsForDestinations();
        }

        public initializeBlockInfo(size?: number, page?: number): ng.IPromise<IMatrixRoomInfo> {
            return this.getAllBlockInfo(undefined, size, page)
                .then((matrixRoomInfo: IMatrixRoomInfo) => {
                    return matrixRoomInfo;
                });
        }

        public initializeSourcesInfo(size?: number,
            page?: number,
            filteredCategories?: string[],
            filteredLevel?: string): ng.IPromise<IMatrixRoomInfo> {
            return this.getAllBlockInfo("source", size, page, filteredCategories, filteredLevel)
                .then((matrixRoomInfo: IMatrixRoomInfo) => {
                    return matrixRoomInfo;
                });
        }

        public initializeSinksInfo(size?: number,
            page?: number,
            filteredCategories?: string[],
            filteredLevel?: string): ng.IPromise<IMatrixRoomInfo> {
            return this.getAllBlockInfo("destination", size, page, filteredCategories, filteredLevel)
                .then((matrixRoomInfo: IMatrixRoomInfo) => {
                    return matrixRoomInfo;
                });
        }

        public updateMatrixRoom(newConnectionRoom: Models.IConnectionRoom): ng.IPromise<{}[]> {
            let updated: ng.IPromise<IMatrixRoomInfo>[] = [];
            let blocksToDelete: IConnectionRoomBlock[] = this.roomsDifference(this.connectionRoom.blockHrefs, newConnectionRoom.blockHrefs);
            let blocksToAdd: IConnectionRoomBlock[] = this.roomsDifference(newConnectionRoom.blockHrefs, this.connectionRoom.blockHrefs);
            let addOrRemoveSource: boolean = false;
            let addOrRemoveSink: boolean = false;
            blocksToDelete.forEach((blockToDelete: Models.IConnectionRoomBlock) => {
                if (blockToDelete.type === 'source') {
                    this.removeSource(Number(blockToDelete.blockHref));
                    this.sourceBlockFilter =
                        new BlocksFilter(this.sources, 'source', this.levelMaps, this.$cookies, this.$filter);
                    addOrRemoveSource = true;
                } else {
                    this.removeDestination(Number(blockToDelete.blockHref));
                    this.destinationBlockFilter =
                        new BlocksFilter(this.destinations, 'destination', this.levelMaps, this.$cookies, this.$filter);
                    addOrRemoveSink = true;
                }
            });

            blocksToAdd.forEach((blockToAdd: Models.IConnectionRoomBlock) => {
                if (blockToAdd.type === 'source') {
                    this.sourcesLoaded = false;
                    addOrRemoveSource = true;
                } else {
                    this.destinationsLoaded = false;
                    addOrRemoveSink = true;
                }
            });
            if (addOrRemoveSource || addOrRemoveSink) {
                updated.push(this.getAllBlockInfo().then((matrixRoom: IMatrixRoomInfo) => {
                    this.sourcesLoaded = true;
                    this.destinationsLoaded = true;
                    this.updateConnectionRoom(newConnectionRoom);
                    return matrixRoom;
                }));
            }
            return this.$q.all(updated);
        }

        public async addMatrixRoomConnection(sourceId: number, sinkId: number, connRoomTakeResult?: ConnRoomTakeResult): Promise<void> {
            const blocks: IBlockComplete[] = await this.loadBlocksIfMissing([sourceId, sinkId]);
            let sourceBlock: IBlockComplete = blocks[0];
            let sinkBlock: IBlockComplete = blocks[1];
            if (!this.matrixRoomConnectedBlocks[sourceId]) {
                this.matrixRoomConnectedBlocks[sourceId] = [];
            }
            this.matrixRoomConnectedBlocks[sourceId].push({
                id: sinkBlock.id,
                name: sinkBlock.name,
            });

            if (!this.matrixRoomConnectedBlocks[sinkId]) {
                this.matrixRoomConnectedBlocks[sinkId] = [];
            }
            if(this.containsBlockAndGet(sinkId) !== undefined) {
                this.matrixRoomConnectedBlocks[sinkId].push({
                    id: sourceBlock.id,
                    name: sourceBlock.name,
                });
            }
            else {
                const testConnectedBlock: IConnectedBlock = {
                    id: sourceBlock.id,
                    name: sourceBlock.name,
                    href: sourceBlock.href,
                }
                this.matrixRoomConnectedBlocks[sinkId-1].push(testConnectedBlock);
            }
        }

        public removeMatrixRoomConnection(sourceId: number, sinkId: number): void {
            if (this.matrixRoomConnectedBlocks[sourceId]) {
                this.matrixRoomConnectedBlocks[sourceId] = this.matrixRoomConnectedBlocks[sourceId].filter((connectedBlock: IConnectedBlock) => connectedBlock.id !== sinkId);
            }
            if (this.matrixRoomConnectedBlocks[sinkId]) {
                if (this.getDestinationBlock(sinkId) === undefined) {
                    this.matrixRoomConnectedBlocks[sinkId-1] = this.matrixRoomConnectedBlocks[sinkId-1].filter((connectedBlock: IConnectedBlock) => connectedBlock.id !== sourceId);
                }
                this.matrixRoomConnectedBlocks[sinkId] = this.matrixRoomConnectedBlocks[sinkId].filter((connectedBlock: IConnectedBlock) => connectedBlock.id !== sourceId);
            }
        }

        public updateBlock(savedBlock: ISaveBlockEventData): void {
            this.sources.forEach((block: IBlockComplete) => {
                if (block.id === savedBlock.id) {
                    block.name = savedBlock.name;
                    block.locked = savedBlock.locked;
                    if (block.connectionStatus !== savedBlock.connectionStatus) {
                        block.videoFeed.thumbnailUrl = Models.VideoFeed.setThumbnailPerStatus(block.videoFeed);
                    }
                    block.connectionStatus = savedBlock.connectionStatus;

                    if (this.updateBlockCategories(block, savedBlock.categories))
                        this.sourceBlockFilter = new BlocksFilter(this.sources, 'source', this.levelMaps, this.$cookies, this.$filter);
                }
            });
            this.destinations.forEach((block: IBlockComplete) => {
                if (block.id === savedBlock.id) {
                    block.name = savedBlock.name;
                    block.locked = savedBlock.locked;
                    if (block.connectionStatus !== savedBlock.connectionStatus) {
                        block.videoFeed.thumbnailUrl = Models.VideoFeed.setThumbnailPerStatus(block.videoFeed);
                    }
                    block.connectionStatus = savedBlock.connectionStatus;

                    if (this.updateBlockCategories(block, savedBlock.categories))
                        this.destinationBlockFilter =
                            new BlocksFilter(this.destinations, 'destination', this.levelMaps, this.$cookies, this.$filter);
                }
            });

        }

        public blockName(id: number): Models.IConnectionRoomBlock {
            let response: Models.IConnectionRoomBlock;
            response = this.getBlockNameFromBlocks(id);
            if (!response) response = this.getBlockNameFromConnectedBlocks(id);
            return response ? response : undefined;
        }

        private getBlockNameFromBlocks(id: number): Models.IConnectionRoomBlock {
            let blocks: IBlockComplete[] = this.sources.concat(this.destinations);
            for (const block of blocks) {
                if (block.id === Number(id)) {
                    return {
                        blockHref: id.toString(),
                        type: block.type,
                        name: block.name,
                    };
                }
            }
        }

        private getBlockNameFromConnectedBlocks(id: number): Models.IConnectionRoomBlock {
            for (const blockId of Object.keys(this.matrixRoomConnectedBlocks)) {
                if (this.matrixRoomConnectedBlocks[blockId]) {
                    for (const connectedBlock of this.matrixRoomConnectedBlocks[blockId]) {
                        if (connectedBlock.id === id) {
                            return {
                                blockHref: id.toString(),
                                type: "",
                                name: connectedBlock.name,
                            };
                        }
                    }
                }
            }
        }

        public updateSourceBlockCategories(blockId: number, categories: Models.Category[]): void {
            this.sources.forEach((source: IBlockComplete) => {
                if (source.id === blockId) {
                    source.categories = categories;
                }
            });
            this.sourceBlockFilter = new BlocksFilter(this.sources, 'source', this.levelMaps, this.$cookies, this.$filter);
        }

        public updateDestinationBlockCategories(blockId: number, categories: Models.Category[]): void {
            this.destinations.forEach((destination: IBlockComplete) => {
                if (destination.id === blockId) {
                    destination.categories = categories;
                }
            });
            this.destinationBlockFilter = new BlocksFilter(this.destinations, 'destination', this.levelMaps, this.$cookies, this.$filter);
        }

        public inSources(id: number): boolean {
            if (this.sources)
                return this.sources.some(
                    (sourceBlock: IBlockComplete) => {
                        return id === sourceBlock.id;
                    }
                );
        }

        public inDestinations(id: number): boolean {
            if (this.destinations)
                return this.destinations.some(
                    (destinationBlock: IBlockComplete) => {
                        return id === destinationBlock.id;
                    }
                );
        }

        public removeSource(source: number): void {
            let indexToRemove: number = this.sources.length;
            this.sources.forEach(
                (current: IBlockComplete, index: number) => {
                    if (current.id === source)
                        indexToRemove = index;
                }
            );
            this.sources.splice(indexToRemove, 1);
        }

        public removeDestination(destination: number): void {
            let indexToRemove: number = this.destinations.length;
            this.destinations.forEach(
                (current: IBlockComplete, index: number) => {
                    if (current.id === destination)
                        indexToRemove = index;
                }
            );
            this.destinations.splice(indexToRemove, 1);
        }

        public async updateStreamPipeIdsForDestinations(): Promise<void> {
            // Check if any node name has changed, if so, update it
            this.updateNodeNamesIfChanged();
            this.streamPipeIdsForDestinations = {};
            if (this.destinations) {
                const blockIds: number[] = this.destinations.map((b: IBlockComplete) => b.id);
                const streamPipes: IStreamPipe[] = (await this.streamPipeService.getStreamPipesByBlockList(blockIds))
                    .map((spList: IStreamPipe[]) => spList[0]);
                streamPipes.forEach((sp: IStreamPipe) => {
                    if (sp)
                        this.streamPipeIdsForDestinations[sp.sinkBlockId] = Common.Utils.idFromHref(sp.href)
                });
            }
            // Check if there is any DR5000 with redundancy in order to add the secondary encoder block to the sources label of the decoder
            if (this.matrixRoomConnectedBlocks !== undefined) {
                let redundantBlocks: IRedundantBlocks
                for (const blockId of Object.keys(this.matrixRoomConnectedBlocks)) {
                    if (this.checkIfDR5000Block(blockId)){
                            redundantBlocks = await this.blockService.getRedundantBlocks(Number(blockId));                        
                        if (redundantBlocks.secondaryDecoderBlock) {
                            const secondaryEncoderConnectedBlock: IConnectedBlock = {
                                id: redundantBlocks.secondaryEncoderBlock.id,
                                name: redundantBlocks.secondaryEncoderBlock.name,
                                href: redundantBlocks.secondaryDecoderBlock.href,
                            }
                            this.matrixRoomConnectedBlocks[redundantBlocks.primaryDecoderBlock.id].push(secondaryEncoderConnectedBlock);
                        }
                    }
                }
            }
        }
        
        public checkIfDR5000Block(blockId: string){
            let blockFound: IBlockComplete;
            this.destinations.forEach( (destinationBlock: IBlockComplete)=>{
                if(destinationBlock.id.toString() == blockId){
                    blockFound = destinationBlock;
                };
            });
            // DR5000 nodeType --> 8
            return blockFound ? blockFound.networkNodeTypeHref == "8" : false;
        }

        public getConnectedSourceForDestination(destinationId: number): IConnectedBlock {
            return this.matrixRoomConnectedBlocks[destinationId] && this.matrixRoomConnectedBlocks[destinationId].length > 0
                 ? this.matrixRoomConnectedBlocks[destinationId][0] : undefined;
        }

        public getSourceBlock(sourceId: number): IBlockComplete {
            let [sourceFound]: IBlockComplete[] = this.sources.filter((source: IBlockComplete) => {
                return source.id === sourceId;
            });
            return sourceFound;
        }

        public getDestinationBlock(destinationId: number): IBlockComplete {
            let [destinationFound]: IBlockComplete[] = this.destinations.filter((destination: IBlockComplete) => {
                return destination.id === destinationId;
            });
            return destinationFound;
        }

        public toggleLock(block: IBlockComplete, blockService: Services.BlockService, comment?: string, value?: boolean): void {
            if (block.type === 'source') {
                for (let i: number = 0; i < this.sources.length; i++) {
                    if (this.sources[i].id === block.id) {
                        this.sources[i].locked = value ? value : !this.sources[i].locked;
                        this.sources[i].lockedComment = comment;
                        blockService.updateBlock(this.sources[i]).then((updated: Models.Block) => {
                            this.sources[i].locked = updated.locked;
                            this.sources[i].lockedUser = updated.lockedUser;
                            this.sources[i].lockedTimestamp = updated.lockedTimestamp;
                        });
                        break;
                    }
                }
            } else {
                for (let i: number = 0; i < this.destinations.length; i++) {
                    let lockedOriginally: boolean = this.destinations[i].locked;
                    if (this.destinations[i].id === block.id) {
                        this.destinations[i].locked = value ? value : !this.destinations[i].locked;
                        this.destinations[i].lockedComment = comment;
                        blockService.updateBlock(this.destinations[i]).then((updated: Models.Block) => {
                            this.destinations[i].locked = updated.locked;
                            this.destinations[i].lockedUser = updated.lockedUser;
                            this.destinations[i].lockedTimestamp = updated.lockedTimestamp;
                            // lock source
                            if (this.matrixRoomConnectedBlocks[this.destinations[i].id] &&
                                this.matrixRoomConnectedBlocks[this.destinations[i].id].length > 0) {
                                let sourceBlock: IBlockComplete = this.getSourceBlock(this.matrixRoomConnectedBlocks[this.destinations[i].id][0].id);
                                let sourceAlreadyLocked: boolean = sourceBlock.locked;
                                if (!lockedOriginally && !sourceAlreadyLocked && sourceBlock !== undefined)
                                    this.toggleLock(sourceBlock, blockService, comment, true);
                            }
                        });
                        break;
                    }
                }
            }
        };

        public changeBlockCategories(block: IBlockComplete, categoriesFromModal: Directives.ICategoriesModalResult): ng.IPromise<void> {
            let addedCategoriesPromise: ng.IPromise<Models.Category>[] =
                categoriesFromModal.categoriesToAdd.map((category: string) => {
                    return this.blockService.
                        addCategoryToBlock(block.id.toString(),
                            new Models.Category(category));
                });
            let deletedCategoriesPromise: ng.IPromise<Models.Category>[] =
                categoriesFromModal.categoriesToDelete.map((category: string) => {
                    return this.blockService.
                        removeCategoryFromBlock(block.id.toString(),
                            new Models.Category(category));
                });
            return this.$q.all(addedCategoriesPromise.concat(deletedCategoriesPromise))
                .then((categories: Models.Category[]) => {
                    this.refreshBlockCategories(block);
                });
        }

        public updateConnectionRoom(newConnectionRoom: Models.IConnectionRoom): void {
            this.connectionRoom = angular.copy(newConnectionRoom);
            this.updateStreamPipeIdsForDestinations();
            // this.addConnectedSourceToDestinations();
        }

        public addBlocksByNodeId(nodeId: number): ng.IPromise<IBlock[]> {
            return this.blockService.blocksInNode(nodeId).then((blocks: Models.IBlock[]) => {
                blocks.forEach((block: Models.IBlock) => {
                    if (!this.containsBlock(block.id)) {
                        if (block.blockType.pipePosition === "SINK") {
                            this.destinations.push(block);
                        } else if (block.blockType.pipePosition === "SOURCE") {
                            this.sources.push(block);
                        }
                    }
                });
                this.updateConnectionRoom(this.connectionRoom);
                return blocks;
            });
        }

        public removeBlocksByNodeId(nodeId: number): void {
            this.sources.filter((blockComplete: IBlockComplete) => {
                return "" + blockComplete.networkNodeHref === "" + nodeId;
            }).forEach((blockComplete: IBlockComplete) => {
                this.removeSource(blockComplete.id);
                this.sourceBlockFilter = new BlocksFilter(this.sources, 'source', this.levelMaps, this.$cookies, this.$filter);
            });
            this.destinations.filter((blockComplete: IBlockComplete) => {
                return blockComplete.networkNodeHref === "" + nodeId;
            }).forEach((blockComplete: IBlockComplete) => {
                this.removeDestination(blockComplete.id);
                this.destinationBlockFilter = new BlocksFilter(this.destinations, 'destination',
                    this.levelMaps, this.$cookies, this.$filter);
            });

        }

        public containsBlocks(blockIds: number[]): boolean {
            for (const blockId of blockIds) {
                if (!this.containsBlock(blockId)) {
                    return false;
                }
            }
            return true;
        }

        public containsBlock(blockId: number): boolean {
            return this.containsDestination(blockId)
                || this.containsSource(blockId)
                || this.containsConnectedBlock(blockId);
        }

        public containsBlockAndGet(blockId: number): IBlockComplete {
            const sourceFound: IBlockComplete[] = this.sources.filter((blockComplete: IBlockComplete) => {
                return blockComplete.id && blockComplete.id === blockId;
            });
            if (sourceFound.length > 0) {
                return sourceFound[0];
            } else {
                const sinkFound: IBlockComplete[] = this.destinations.filter((blockComplete: IBlockComplete) => {
                    return blockComplete.id && blockComplete.id === blockId;
                });
                if (sinkFound.length > 0) return sinkFound[0];
            }
        }

        public containsDestination(destination: number): boolean {
            return this.destinations.some((blockComplete: IBlockComplete) => {
                return blockComplete.id && blockComplete.id === destination;
            });
        }

        public containsSource(source: number): boolean {
            return this.sources.some((blockComplete: IBlockComplete) => {
                return blockComplete.id && blockComplete.id === source;
            });
        }

        public containsConnectedBlock(blockId: number): boolean {
            return this.matrixRoomConnectedBlocks[blockId] !== undefined;
        }

        public async takeEvent(source: number, destination: number, streamPipeId?: number): Promise<void> {
            await this.addMatrixRoomConnection(source, destination);
            if (streamPipeId !== undefined)
                this.streamPipeIdsForDestinations[destination] = streamPipeId;

        }

        public takeEventNotContainingSink(source: number, destination: number, streamPipeId?: number): void {
            this.connectionRoomService.getMatrixRoomBlockConnections(destination)
                .then((blockConnections: Models.IConnectedBlock[]) => {
                    this.matrixRoomConnectedBlocks[destination] = blockConnections;
                }).catch((reason: ngCustom.IReason) => {
                    console.log(reason);
                });
        }

        public dropEvent(source: number, destination: number, droppedInEvent?: boolean): void {
            if (droppedInEvent) {
                this.streamPipeIdsForDestinations[destination] = undefined;
                this.removeMatrixRoomConnection(source, destination);
            }
        }

        public refreshBlocks(blocks: Models.IBlockComplete[]): void {
            blocks.filter((block: Models.IBlockComplete) => block !== undefined)
                .forEach((block: Models.IBlockComplete) => {
                    this.refreshBlock(block);
                });
        }

        public refreshBlock(block: Models.IBlockComplete): void {
            this.blockService.findById(block.id.toString()).then((freshBlock: Models.IBlock) => {
                block.name = freshBlock.name;
                block.application = freshBlock.application;
                block.blockConnections = freshBlock.blockConnections;
                block.lockedComment = freshBlock.lockedComment;
                block.locked = freshBlock.locked;
                block.lockedUser = freshBlock.lockedUser;
                block.lockedTimestamp = freshBlock.lockedTimestamp;
                block.config = freshBlock.config;
            });
        }

        public pushBlockIfMissing(blockId: number): void {
            let exists: boolean = this.matrixRoomConnectedBlocks[blockId] !== undefined;
            if (!exists) {
                const newBlock: Models.IBlock = this.fetchBlockFromAllStorages(blockId);
                this.matrixRoomConnectedBlocks[blockId] = [newBlock];
            }
        }

        public addAllSourceBlockInfoPageToMatrix(size?: number,
                                                 page?: number,
                                                 filteredCategories?: string[],
                                                 filteredLevel?: string): ng.IPromise<IMatrixRoomInfo> {
            const type: string = "source";
            return this.connectionRoomService.getMatrixRoomSources(this.connectionRoom, size, page, filteredCategories, filteredLevel)
                .then((matrix: IMatrixRoomInfo) => {
                    matrix = this.afterCallMatrixRoomPromise(type, matrix);
                    return matrix;
                });
        }

        public addAllSinkBlockInfoPageToMatrix(size?: number,
                                               page?: number,
                                               filteredCategories?: string[],
                                               filteredLevel?: string): ng.IPromise<IMatrixRoomInfo> {
            const type: string = "destination";
            return this.connectionRoomService.getMatrixRoomSinks(this.connectionRoom, size, page, filteredCategories, filteredLevel)
                .then((matrix: IMatrixRoomInfo) => {
                    matrix = this.afterCallMatrixRoomPromise(type, matrix);
                    return matrix;
                });
        }

        private afterCallMatrixRoomPromise(type: string, matrix: IMatrixRoomInfo): IMatrixRoomInfo {
            let promised: IBlockComplete[] = matrix.blocks;
            promised.forEach((block: IBlockComplete) => {
                if (block.videoFeed) {
                    block.videoFeed.thumbnailUrl = Models.VideoFeed.setThumbnailPerStatus(block.videoFeed);
                }
            });

            if (!this.sources) {
                this.sources = [];
            }
            if (!this.destinations) {
                this.destinations = [];
            }

            if (type === "source") {
                if (promised && promised.length > 0) {
                    this.sources.push(...this.pushWithoutDuplicatesByKey(this.sources, promised, 'id'));
                }
                if (this.sources.length > 0) {
                    this.sourceBlockFilter =
                        new BlocksFilter(this.sources, 'source', this.levelMaps,
                            this.$cookies, this.$filter,
                            this.sourceBlockFilter ? this.sourceBlockFilter.searchInput : undefined);
                }
            } else {
                if (promised && promised.length > 0) {
                    this.destinations.push(...this.pushWithoutDuplicatesByKey(this.destinations, promised, 'id'));
                }
                if (this.destinations.length > 0) {
                    this.destinationBlockFilter =
                        new BlocksFilter(this.destinations, 'destination', this.levelMaps,
                            this.$cookies, this.$filter,
                            this.destinationBlockFilter ? this.destinationBlockFilter.searchInput : undefined);
                }
            }

            if (!this.matrixRoomConnectedBlocks) {
                this.matrixRoomConnectedBlocks = {};
            }
            this.mergeConnectedBlocks(matrix.connectedBlocks);
            // this.addConnectedSourceToDestinations();

            return matrix;
        }

        private refreshBlockCategories(block: Models.IBlockComplete): void {
            this.blockService.getCategoriesByBlockId(block.id.toString()).then((blockCategories: Models.Category[]) => {
                if (block.type === 'source') {
                    for (let i: number = 0; i < this.sources.length; i++) {
                        if (this.sources[i].id === block.id) {
                            this.sources[i].categories = blockCategories.length > 0 ? blockCategories : [];
                        }
                    }
                    this.sourceBlockFilter.refreshCategories();
                } else {
                    for (let i: number = 0; i < this.destinations.length; i++) {
                        if (this.destinations[i].id === block.id) {
                            this.destinations[i].categories = blockCategories.length > 0 ? blockCategories : [];
                        }
                    }
                    this.destinationBlockFilter.refreshCategories();
                }
            });
        }


        private getAllCategories(): void {
            this.blockService.getCategoriesWithoutBlocks().then((allCategories: Models.Category[]) => {
                this.allCategories = allCategories;
            });
        }

        /**
         * Method to check if any node name has been changed
         */
        private updateNodeNamesIfChanged(): void {
            if(this.matrixRoomConnectedBlocks !== undefined) {
                for (const blockId of Object.keys(this.matrixRoomConnectedBlocks)) {
                    let connectedBlocks: IConnectedBlock[] = this.matrixRoomConnectedBlocks[blockId];
                    for (const bl of connectedBlocks) {
                        if (bl.id !== undefined) {
                            let connectedBlockFromBackend = this.getSourceBlock(bl.id) !== undefined ?
                                this.getSourceBlock(bl.id) : this.getDestinationBlock(bl.id);
                            if (connectedBlockFromBackend !== undefined && bl.name !==  connectedBlockFromBackend.name) {
                                bl.name = connectedBlockFromBackend.name;
                            }
                        }
                    }
                }
            }
        }

        private getAllBlockInfo(type?: string,
                                size?: number,
                                page?: number,
                                filteredCategories?: string[],
                                filteredLevel?: string): ng.IPromise<IMatrixRoomInfo> {
            let matrixRoomPromises: ng.IPromise<IMatrixRoomInfo>[] = [];
            if (!type || type === "source") {
                this.sourcesLoaded = false;
                matrixRoomPromises.push(this.connectionRoomService
                    .getMatrixRoomSources(this.connectionRoom, size, page, filteredCategories, filteredLevel));
            }
            if (!type || type === "destination") {
                this.destinationsLoaded = false;
                matrixRoomPromises.push(this.connectionRoomService
                    .getMatrixRoomSinks(this.connectionRoom, size, page, filteredCategories, filteredLevel));
            }

            return this.$q.all(matrixRoomPromises).then((resolved: IMatrixRoomInfo[]) => {
                let matrix: IMatrixRoomInfo;
                if (resolved[0]) {
                    matrix = type
                        ? this.afterCallMatrixRoomPromise(type, resolved[0])
                        : this.afterCallMatrixRoomPromise("source", resolved[0]);
                }
                if (resolved[1]) {
                    matrix = type
                        ? this.afterCallMatrixRoomPromise(type, resolved[1])
                        : this.afterCallMatrixRoomPromise("destination", resolved[1]);
                }
                return matrix;
            });
        }

        private pushWithoutDuplicatesByKey(parentArray: any[], arrayToAdd: any[], key: string): any[] {
            let returnedArray: any[] = [];
            if (!parentArray) {
                parentArray = [];
            }
            if (parentArray.length > 0) {
                for (let i: number = 0; i < arrayToAdd.length; i++) {
                    if (parentArray.filter((obj: any) => obj[key] === arrayToAdd[i][key]).length > 0) {
                        continue;
                    }
                    returnedArray.push(arrayToAdd[i]);
                }
            } else {
                returnedArray.push(...arrayToAdd);
            }
            return returnedArray;
        }

        private getAllSourceBlocksObjectsPromise(size?: number,
                                                 page?: number,
                                                 filteredCategories?: string[],
                                                 filteredLevel?: string): ng.IPromise<IBlockComplete[]> {
            return this.connectionRoomService.getMatrixRoomSources(this.connectionRoom,
                size, page, filteredCategories, filteredLevel).then((matrixRoomInfo: IMatrixRoomInfo) => {
                    return matrixRoomInfo.blocks;
                });
        }

        private getAllSinkBlocksObjectsPromise(size?: number,
                                               page?: number,
                                               filteredCategories?: string[],
                                               filteredLevel?: string): ng.IPromise<IBlockComplete[]> {
            return this.connectionRoomService.getMatrixRoomSinks(this.connectionRoom,
                size, page, filteredCategories, filteredLevel).then((matrixRoomInfo: IMatrixRoomInfo) => {
                    return matrixRoomInfo.blocks;
                });
        }

        private updateBlockCategories(block: IBlockComplete, categories: string[]): boolean {
            let modifiedCategories: boolean = false;
            categories.forEach((category: string) => {
                let cat: ICategory = { name: category };
                if (!block.categories.some((blockCategory: ICategory) => {
                    return blockCategory.name === category;
                })) {
                    block.categories.push(cat);
                    modifiedCategories = true;
                }
                this.updateAllCategories(cat);
            });
            block.categories.forEach((blockCategory: ICategory, index: number) => {
                if (!categories.some((category: string) => {
                    return blockCategory.name === category;
                })) {
                    block.categories.splice(index, 1);
                    modifiedCategories = true;
                }
            });

            return modifiedCategories;
        }

        private updateAllCategories(newCategory: ICategory): void {
            let found: boolean = this.allCategories.some((cat: ICategory) => {
                return cat.name === newCategory.name;
            });
            if (!found) {
                this.allCategories.push(newCategory);
            }
        }

        private roomsDifference(a: IConnectionRoomBlock[], b: IConnectionRoomBlock[]): IConnectionRoomBlock[] {
            let diff: IConnectionRoomBlock[] = a.filter((aElem: IConnectionRoomBlock) => {
                return b.every((bElem: IConnectionRoomBlock) => {
                    return aElem.blockHref !== bElem.blockHref || aElem.type !== bElem.type;
                });
            });
            return diff;
        };


        private async loadBlocksIfMissing(blockIds: number[], forceLoad: boolean = false): Promise<Models.IBlock[]> {
            const loadedBlocks: Models.IBlock[] = [];
            for (let blockId of blockIds) {
                if (forceLoad || !(this.containsSource(blockId) || this.containsDestination(blockId))) {
                    try {
                        let block: Models.IBlock = await this.blockService.findById(blockId.toString());
                        loadedBlocks.push(block);
                        // this.matrixRoomConnectedBlocks.push(block);
                    } catch (e) {
                        // Don't have permissions to fetch the block, we were getting the whole matrix room before. Currently, do nothing.
                    }
                } else {
                    const block: IBlockComplete = this.containsBlockAndGet(blockId);
                    if (block) loadedBlocks.push(block);
                }
            }
            return loadedBlocks;
        }

        private fetchBlockFromAllStorages(blockId: number): IBlockComplete {
            return this.getSourceBlock(blockId) || this.getDestinationBlock(blockId);
        }

        private mergeConnectedBlocks(connectedBlocksMap: IConnectedBlocksMap): void {
            for (const blockId of Object.keys(connectedBlocksMap)) {
                if (!this.matrixRoomConnectedBlocks[blockId]) {
                    this.matrixRoomConnectedBlocks[blockId] = connectedBlocksMap[blockId];
                } else {
                    this.matrixRoomConnectedBlocks[blockId]
                        .push(...this.pushWithoutDuplicatesByKey(this.matrixRoomConnectedBlocks[blockId],
                            connectedBlocksMap[blockId],
                            "id"));
                }
            }
        }
    }
}
